import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiSend } from 'react-icons/fi';

const bots = [
    {
        nameKey: 'finance',
        label: 'finance',
        commands: [
            { input: 'caffè 2.50', desc: 'Quick expense → inline keyboard (account → category → save)' },
            { input: '📎 photo / PDF', desc: 'OCR import via Gemini → review extracted transactions' },
            { input: '⚠️ Finance alert', desc: 'Subscriptions/PAC due tomorrow with low balance' },
        ],
    },
    {
        nameKey: 'tracker',
        label: 'tracker',
        commands: [
            { input: 'https://... <tag>', desc: 'Save a link to the inbox with optional tags' },
            { input: '🔔 Habit reminder', desc: 'Daily nudge for habits not yet logged today' },
            { input: '/log lettura 35', desc: 'Quick-log a habit value by name' },
        ],
    },
    {
        nameKey: 'diet',
        label: 'diet',
        commands: [
            { input: '🍽️ Meal reminder', desc: 'Meal notifications with ingredients and macros via Markdown' },
            { input: '✅ / 🔄 / ❌', desc: 'Log the meal as planned, swapped or skipped straight from the card' },
            { input: '/oggi', desc: "Today's meals with their logged status and the plan total" },
        ],
    },
    {
        nameKey: 'shortener',
        label: 'shortener',
        commands: [
            { input: 'https://...', desc: 'Create short link → go.rontaro.com/<code>' },
            { input: 'https://... mycode', desc: 'Create link with custom code' },
            { input: '/list', desc: 'Last 20 links with click count' },
            { input: '/del <code>', desc: 'Delete a link' },
        ],
    },
    {
        nameKey: 'system',
        label: 'system',
        commands: [
            { input: '🔗 [Sistema] New link', desc: 'Guest link created — URL and originating IP logged' },
            { input: '⚠️ [Sistema] Rate limit', desc: 'IP blocked for excessive requests on the shortener' },
            { input: '🗄️ [Sistema] R2 alert', desc: 'Storage usage above threshold on Workers R2 bucket' },
            { input: '🧪 [Sistema] Test', desc: 'Manual ping from the admin monitoring panel' },
        ],
    },
    {
        nameKey: 'contact',
        label: 'contact',
        commands: [
            { input: '📬 New message', desc: 'Instant notification when someone submits the contact form' },
            { input: '👤 Name + Email + Message', desc: 'Full message details delivered directly to Telegram' },
        ],
    },
    {
        nameKey: 'infraAlerts',
        label: 'infra-alerts',
        commands: [
            { input: '💾 Backup completed / ❌ failed', desc: 'Daily Raspberry Pi backup status — confirms success or alerts on failure' },
            { input: '📈 Alert rule fired / ✅ resolved', desc: 'Grafana metric thresholds exceeded or recovered (CPU, memory, disk)' },
            { input: '🔴 Service down / 🟢 up', desc: 'Uptime Kuma monitoring — instant down/up alerts with downtime duration' },
        ],
    },
];

const TelegramBots = () => {
    const { t } = useTranslation();

    return (
        <section id="bots" className="relative section">
            {/* Background orbs for continuity */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div 
                    animate={{ x: [0, -40, 0], y: [0, 70, 0] }} 
                    transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute bottom-1/3 -left-20 w-80 h-80 bg-blue-200/20 dark:bg-blue-900/10 rounded-full blur-[100px]"
                />
            </div>

            <div className="container-custom relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="mb-12">
                        <span className="section-number">05 — Telegram Bots</span>
                        <h2 className="section-title mt-2">{t('bots.sectionTitle')}</h2>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {bots.map((bot, bi) => (
                            <motion.div
                                key={bi}
                                className="terminal-card overflow-hidden"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: bi * 0.1 }}
                            >
                                <div className="terminal-header">
                                    <span className="terminal-dot bg-red-400/80" />
                                    <span className="terminal-dot bg-amber-400/80" />
                                    <span className="terminal-dot bg-green-400/80" />
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-3">bot_{bot.label}</span>
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                                            <FiSend size={18} className="text-amber-500 dark:text-amber-400" />
                                        </div>
                                        <h3 className="font-bold text-zinc-900 dark:text-zinc-50">{t(`bots.items.${bot.nameKey}.name`)}</h3>
                                    </div>
                                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6 leading-relaxed">
                                        {t(`bots.items.${bot.nameKey}.description`)}
                                    </p>

                                    <div className="space-y-4">
                                        {bot.commands.map((cmd, ci) => (
                                            <div key={ci} className="space-y-1.5 p-3 rounded-xl bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200/50 dark:border-zinc-700/50">
                                                <code className="block font-mono text-[11px] text-amber-600 dark:text-amber-400 font-bold whitespace-pre-wrap">
                                                    {cmd.input}
                                                </code>
                                                <p className="text-[11px] text-zinc-500 dark:text-zinc-500 leading-snug font-medium">
                                                    {cmd.desc}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default TelegramBots;
